import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import {
  ApprovalSchemeFormDto,
  type ApprovalSchemeFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-form.dto';
import { ApprovalSchemeTypeEnum } from '@zaoblako/shared/modules/catalog/enums/approval-scheme-type.enum';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ApprovalSchemeService } from '@zaoblako/web/core/services/budget/approval-scheme.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserAndGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-and-group-multi-select/user-and-group-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { Observable } from 'rxjs';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'crm-approval-scheme-form',
  standalone: true,
  imports: [
    CommonModule,
    InputTextComponent,
    JoinPipe,
    SelectButtonModule,
    FormsModule,
    TableComponent,
    TableModule,
    UserMultiSelectComponent,
    ButtonComponent,
    CardModule,
    UserAndGroupMultiSelectComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    CheckboxModule
  ],
  templateUrl: './approval-scheme-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalSchemeFormComponent extends BaseFormComponent<ApprovalSchemeFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public scheme = <ApprovalSchemeFormInterface>{};
  public dto = ApprovalSchemeFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public typeList: { label: string; value: ApprovalSchemeTypeEnum }[] = [
    { label: 'Последовательный', value: ApprovalSchemeTypeEnum.SEQUENTIAL },
    { label: 'Параллельный', value: ApprovalSchemeTypeEnum.PARALLEL }
  ];

  public columns: CrmTableColumn[] = [{}, { label: 'Пользователь' }];
  public auditors = [];
  public accessUsers = [];

  public get ApprovalSchemeTypeEnum() {
    return ApprovalSchemeTypeEnum;
  }

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly approvalSchemeService: ApprovalSchemeService,
    private readonly cdRef: ChangeDetectorRef,
    public readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.approvalSchemeService
      .findById<ApprovalSchemeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (scheme) => {
          this.scheme = scheme;
          if (this.scheme.userAuditors?.length) {
            this.scheme.userAuditors.forEach((user) => this.auditors.push({ user }));
          }
          if (this.scheme.userGroupAuditors?.length) {
            this.scheme.userGroupAuditors.forEach((group) => this.auditors.push({ group }));
          }

          if (this.scheme.userAccess?.length) {
            this.scheme.userAccess.forEach((user) => this.accessUsers.push({ user }));
          }
          if (this.scheme.groupAccess?.length) {
            this.scheme.groupAccess.forEach((group) => this.accessUsers.push({ group }));
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public onChangeUsersAndGroup(key: 'auditor' | 'access') {
    if (key === 'auditor') {
      this.scheme.userAuditors = [];
      this.scheme.userGroupAuditors = [];
      this.auditors.forEach((auditor) => {
        auditor.user ? this.scheme.userAuditors.push(auditor.user) : this.scheme.userGroupAuditors.push(auditor.group);
      });
    } else {
      this.scheme.userAccess = [];
      this.scheme.groupAccess = [];
      this.accessUsers.forEach((accessUser) => {
        accessUser.user ? this.scheme.userAccess.push(accessUser.user) : this.scheme.groupAccess.push(accessUser.group);
      });
    }
  }

  public saveScheme() {
    if (this.checkValidate(this.scheme).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.approvalSchemeService
          .update<ApprovalSchemeFormInterface, ApprovalSchemeFindInterface>(this.scheme)
          .subscribe({
            next: (scheme) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Схема согласования "${this.scheme.name}" обновлена`);
              this.formStoreService.saveNext(scheme, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.approvalSchemeService.create<ApprovalSchemeFindInterface>(this.scheme).subscribe({
          next: (scheme) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Схема согласования создана');
            this.formStoreService.saveNext(scheme, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteScheme() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить схему согласования "${this.scheme.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.approvalSchemeService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Схема согласования "${this.scheme.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
